/**
 * Transport Access Wrapper Component
 *
 * Checks if user has transport unit access before rendering the TransporterModule.
 * This wrapper properly handles the access check without breaking React hooks rules.
 *
 * Follows the same pattern as GaushalaAccessWrapper.
 */

import React, { useEffect, useState, useRef } from 'react';
import TransporterModule from './Transporter.container';
import NoUnitAssigned from '@/components/access/NoUnitAssigned';
import { getTransportProfile, TransportError } from '@/services/transport/profileService';
import { Truck } from 'lucide-react';

type AccessState = 'checking' | 'allowed' | 'denied';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * TransportAccessWrapper Component
 * Renders the appropriate component based on transport unit access status
 */
const TransportAccessWrapper: React.FC = () => {
  const [accessState, setAccessState] = useState<AccessState>('checking');
  const retryCount = useRef(0);

  // Check transport access on mount with retry for transient errors
  useEffect(() => {
    const checkAccess = async () => {
      try {
        await getTransportProfile();
        setAccessState('allowed');
      } catch (error) {
        // Explicit "no unit assigned" — deny immediately, no retry (retrying
        // won't change the assignment state on the backend).
        if (error instanceof TransportError && error.code === 'NO_UNIT_ASSIGNED') {
          setAccessState('denied');
        } else if (retryCount.current < MAX_RETRIES) {
          // Transient/network error — retry after a short delay (handles post-login race condition)
          retryCount.current += 1;
          console.warn(`Transport access check failed (attempt ${retryCount.current}/${MAX_RETRIES}), retrying...`, error);
          setTimeout(checkAccess, RETRY_DELAY_MS);
        } else {
          // Exhausted retries — fail closed. Previously allowed access on unknown
          // error which let users through with all API calls failing downstream.
          console.error('Transport access check failed after retries, denying access:', error);
          setAccessState('denied');
        }
      }
    };

    checkAccess();
  }, []);

  // Show loading spinner
  if (accessState === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Transport...</p>
        </div>
      </div>
    );
  }

  // Show no unit page if denied
  if (accessState === 'denied') {
    return (
      <NoUnitAssigned
        moduleName="Transport Unit"
        icon={Truck}
        theme={{
          gradient: 'from-blue-50 via-indigo-50 to-purple-50',
          cardBorder: 'border-blue-200',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
        }}
      />
    );
  }

  // Show main transport module if allowed
  return <TransporterModule />;
};

export default TransportAccessWrapper;
